import React, { Children, isValidElement, cloneElement } from "react";
import cn from "classnames";
import { useComboxContext } from "~/components/shared/Combox/ComboxContext";
import { ComboxEmptyState } from "~/components/shared/Combox/ComboxEmptyState";
import { ComboxListGroup } from "~/components/shared/Combox/ComboxListGroup";
import { ComboxItem } from "~/components/shared/Combox/ComboxItem";

interface ComboxLoadingProps {
  className?: string;
  lines?: number;
}

const ComboxLoading = ({ className, lines = 3 }: ComboxLoadingProps) => {
  return (
    <div className={cn("flex h-full w-full flex-col justify-center gap-2 px-2", className)}>
      {Array.from({ length: lines }, (item, index) => (
        <div
          key={`line-${index}`}
          className={cn(
            "h-4 animate-pulse rounded-sm bg-gray-300",
            index === 0 && "w-1/4",
            index === 1 && "w-3/4",
            index === 2 && "w-1/2",
          )}
        />
      ))}
    </div>
  );
};

export interface ComboxListProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  emptyState?: React.ReactNode;
  loading?: boolean;
  filterItems?: boolean;
  loadingState?: React.ReactNode;
  showEmptyStateWhenNoItems?: boolean;
}

export const ComboxList = ({
  children,
  className,
  style,
  emptyState = "No results found",
  loading = false,
  filterItems = true,
  loadingState,
  showEmptyStateWhenNoItems = false,
  ...props
}: ComboxListProps) => {
  const { searchQuery, multiple } = useComboxContext();

  // Filter children based on search query (unless filterItems is false for server-side filtering)
  const filteredChildren = React.useMemo(() => {
    if (!searchQuery || !filterItems) {
      return children;
    }

    const filterChild = (child: React.ReactNode): boolean => {
      if (!isValidElement(child)) {
        return false;
      }

      // Extract text content for filtering
      const extractText = (node: React.ReactNode): string => {
        if (typeof node === "string") {
          return node;
        }
        if (typeof node === "number") {
          return String(node);
        }
        if (isValidElement(node)) {
          // Handle Emojify component
          if (typeof node === "object" && node?.props?.text) {
            return node.props.text;
          }
          // Handle dangerouslySetInnerHTML for complex content
          if (node.props.dangerouslySetInnerHTML?.__html) {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = node.props.dangerouslySetInnerHTML.__html;
            return tempDiv.textContent || tempDiv.innerText || "";
          }
          if (node.props.children) {
            return Children.toArray(node.props.children).map(extractText).join(" ");
          }
        }
        return "";
      };

      const text = extractText(child);
      return text.toLowerCase().includes(searchQuery.toLowerCase());
    };

    return Children.toArray(children).filter(filterChild);
  }, [children, searchQuery, filterItems]);

  // Add indices to filtered items (only actual items, not groups)
  const childrenWithIndices = React.useMemo(() => {
    let itemIndex = 0;

    const processChild = (child: React.ReactNode): React.ReactNode => {
      if (!isValidElement(child)) {
        return child;
      }

      // If it's a group, process its children but don't assign an index to the group itself
      if (child.type === ComboxListGroup) {
        return cloneElement(child as React.ReactElement<any>, {
          ...child.props,
          children: Children.map(child.props.children, processChild),
        });
      }

      // Only assign indices to actual items
      if (child.type === ComboxItem) {
        const itemElement = cloneElement(child as React.ReactElement<any>, {
          itemIndex,
          ...child.props,
        });
        itemIndex++;
        return itemElement;
      }

      return child;
    };

    return Children.map(filteredChildren, processChild);
  }, [filteredChildren]);

  const shouldShowEmpty = React.useMemo(() => {
    return (
      !loading &&
      (showEmptyStateWhenNoItems || !!searchQuery) &&
      Children.count(filteredChildren) === 0
    );
  }, [loading, filteredChildren, searchQuery, showEmptyStateWhenNoItems]);

  return (
    <div
      className={cn("flex flex-1 scroll-py-6 flex-col gap-px overflow-y-auto p-2", className)}
      style={style}
      role="listbox"
      aria-multiselectable={multiple ? "true" : "false"}
      {...props}
    >
      {loading ? (
        loadingState || <ComboxLoading />
      ) : shouldShowEmpty ? (
        typeof emptyState === "string" ? (
          <ComboxEmptyState>{emptyState}</ComboxEmptyState>
        ) : (
          emptyState
        )
      ) : (
        childrenWithIndices
      )}
    </div>
  );
};

export default ComboxList;
